<template>
  <div class="w-full h-full pt-5">
    <div class="flex items-center px-4">
      <back-button
        label="Back"
        variant="secondary"
        @onClick="$router.back()"
        class="mr-3"
      />
      <div class="divider-line mr-3"></div>
      <h1 class="text-xl text-left font-extrabold text-darkPurple mr-4">
        Organisation Design
      </h1>
      <Breadcrumb :items="breadcrumbs" />
    </div>

    <div class="mx-3 mt-5">
      <Card class="p-4" :footer-has-border="false" style="border-radius: 16px;">
        <div class="flex items-center gap-4 justify-end">
          <Button
            :background="appearance.buttonColor || defaultBTNColor"
            class="text-white"
            @click="openDrawer"
          >
            Add New
          </Button>
          <Button
            background-color="none"
            class="text-dynamicBackBtn border border-solid border-dynamicBackBtn"
            @click="handleImport"
          >
            Import
          </Button>
        </div>

        <CardFooter
          class="mt-4 family-footer"
          :show-sort-filter="true"
          :show-search="true"
          :showfilter="true"
          showcalendar
          search-placeholder="Search"
          reloadcard
          @searchResult="handleSearch"
          @sortType="handleSort"
          @calendar="handleCalendar"
          @reload="handleReload"
        />

        <template v-slot:footer>
          <div class="family-footer pt-4 mt-4 w-full">
            <h2 class="text-lg font-bold text-darkPurple">Job families</h2>
            <p class="text-sm text-jetOpacity">
              Each family maintains its own relationship to job roles and employees.
            </p>
          </div>
        </template>
      </Card>
    </div>

    <div v-if="!showError" class="w-full h-full p-3">
      <NoOrgRegister
        v-if="!loadingData && !jobFamilyRows.length"
        title="No job families have been created yet."
        subtitle="Once a job family is created, it will automatically appear in this register."
      />
      <sTable
        v-else
        :headers="jobFamilyHeaders"
        :items="jobFamilyRows"
        :pagination-list="metaData"
        :loading="loadingData"
        aria-label="job family table"
        class="w-full"
        @page="handlePage($event)"
        @itemsPerPage="handleItemsPerPage($event)"
        has-number
        page-sync
      >
        <template v-slot:item="{ item }">
          <span v-if="item.id">
            <Menu left top="-150" margin="24" class="my-2 p-0">
              <template v-slot:title>
                <icon icon-name="more_icon" size="xs" />
              </template>
              <div style="width: 116px; height: 81px" class="py-3 px-2">
                <div
                  class="flex py-1 px-3 more h-8 cursor-pointer"
                  @click="handleEditJobFamily(item.data)"
                >
                  <icon
                    icon-name="edit"
                    class-name="text-blueCrayola mr-2"
                    size="xsm"
                  />
                  <p class="pt-1">Edit</p>
                </div>
                <div
                  class="flex px-3 py-1 more h-8 cursor-pointer"
                  @click="openDeleteJobFamilyDialog(item.data)"
                >
                  <icon
                    icon-name="delete"
                    class-name="text-desire mr-2"
                    size="xsm"
                  />
                  <p class="pt-1">Delete</p>
                </div>
              </div>
            </Menu>
          </span>
        </template>
      </sTable>
    </div>
    <div v-else>
      <error-page />
    </div>

    <FamilyDrawer
      :is-open="showFamilyDrawer"
      :is-edit="Boolean(selectedJobFamily && selectedJobFamily.id)"
      :initial-data="selectedJobFamily"
      @close="closeDrawer"
      @save="handleSaveFamily"
    />
    <import-employee :open-modal="isOpenImport" @close="isOpenImport = false"
      :isOverwriteEmployee="false" :isTemplate="true" @download="downloadJobClassificationTemplate()"
      @reload-employees="handleReload()" @importEmployee="importDesignation" :url=" url"
      :title="`Import Job Family Data`" :is-employee-directory="false" :isDisplayProgressBar="false"
    />

    <Modal v-if="dialogDeleteJobFamily">
      <Card class="p-5 flex flex-col max-w-md">
        <div>
          <p class="font-bold ml-4 my-5 text-center">
            <template v-if="pendingDeleteJobFamilyName">
              Are you sure you want to delete "{{ pendingDeleteJobFamilyName }}"?
            </template>
            <template v-else>
              Are you sure you want to delete this job family?
            </template>
          </p>
          <div class="flex">
            <Button
              class="text-white"
              :background="appearance.buttonColor || defaultBTNColor"
              :disabled="deletingJobFamily"
              @click="confirmDeleteJobFamily"
            >
              Yes
            </Button>
            <Button
              background-color="none"
              class="text-dynamicBackBtn border border-solid border-dynamicBackBtn ml-4"
              @click="closeDeleteJobFamilyDialog"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    </Modal>
  </div>
</template>

<script>
import { mapState } from "vuex";
import BackButton from "@scelloo/cloudenly-ui/src/components/back-button";
import Breadcrumb from "@scelloo/cloudenly-ui/src/components/bread-crumb";
import STable from "@scelloo/cloudenly-ui/src/components/table";
import Button from "@/components/Button";
import Card from "@/components/Card";
import CardFooter from "@/components/CardFooter";
import Icon from "@/components/Icon";
import Menu from "@/components/Menu";
import ImportEmployee from "@/components/importEmployee";
import Modal from "@/components/Modal";
import ErrorPage from "@/modules/Admin/error403";
import NoOrgRegister from "../NoOrgRegister.vue";
import FamilyDrawer from "./FamilyDrawer";
import { JOB_CLASSIFICATION_TEMPLATE_PATH } from "../constants";

export default {
  name: "Family",
  components: {
    BackButton,
    Breadcrumb,
    STable,
    Button,
    Card,
    CardFooter,
    Icon,
    Menu,
    ImportEmployee,
    Modal,
    NoOrgRegister,
    FamilyDrawer,
    ErrorPage,
  },
  data() {
    return {
      showError: false,
      dialogDeleteJobFamily: false,
      pendingDeleteJobFamilyId: null,
      pendingDeleteJobFamilyName: "",
      deletingJobFamily: false,
      url: `${process.env.VUE_APP_UPLOAD_CLOUDENLY_FILES}/document-management/upload/job-classifications`,
      isOpenImport: false,
      showFamilyDrawer: false,
      selectedJobFamily: null,
      loadingData: true,
      metaData: {
        total: 0,
        page: 1,
        lastPage: 1,
        nextPage: null,
        prevPage: null,
        from: 0,
        to: 0,
      },
      searchWord: null,
      sortType: null,
      numberOfPage: 1,
      itemsPerPage: 20,
      breadcrumbs: [
        {
          disabled: false,
          text: "Organisation Design",
          id: "",
        },
        {
          disabled: false,
          text: "Job families",
          id: "Job families",
        },
      ],
      jobFamilyRows: [],
    };
  },
  computed: {
    jobFamilyHeaders() {
      return [
        { title: "JOB FAMILY", value: "jobFamily" },
        { title: "DESCRIPTION", value: "description" },
        { title: "JOB ROLES COUNT", value: "rolesCount" },
        { title: "EMPLOYEES COUNT", value: "employeesCount" },
        { title: "", value: "id", image: true },
      ];
    },
    ...mapState({
      defaultBTNColor: (state) => state.orgAppearance.defaultBtnColor,
      appearance: (state) => state.orgAppearance.orgAppearanceData,
    }),
  },
  methods: {
    queryUpdate() {
      const search = this.searchWord ? `&search=${this.searchWord}` : "";
      const sort = this.sortType ? `&sort=${this.sortType}` : "";
      const pageNumber = this.numberOfPage ? `&page=${this.numberOfPage}` : "";
      const itemPage = this.itemsPerPage ? `&perPage=${this.itemsPerPage}` : "";
      this.getJobClassifications(`${sort}${search}${pageNumber}${itemPage}`);
    },
    async getJobClassifications(payload = "") {
      this.loadingData = true;
      try {
        const response = await this.$_getJobClassification(payload);
        const classifications = response?.data?.jobClassifications || [];
        this.jobFamilyRows = classifications
          .filter((item) => item.type === "jobFamily")
          .map((item) => ({
            id: item.id,
            jobFamily: item.name,
            description: item.description || "------",
            rolesCount: item.numberOfDesignations ?? 0,
            employeesCount: item.numberOfEmployees ?? 0,
          }));
        this.metaData = response?.data?.meta || {
          total: this.jobFamilyRows.length,
          page: 1,
          lastPage: 1,
          nextPage: null,
          prevPage: null,
          from: this.jobFamilyRows.length ? 1 : 0,
          to: this.jobFamilyRows.length,
        };
      } catch (error) {
        this.jobFamilyRows = [];
        this.metaData = {
          total: 0,
          page: 1,
          lastPage: 1,
          nextPage: null,
          prevPage: null,
          from: 0,
          to: 0,
        };
      } finally {
        this.loadingData = false;
      }
    },
    async importDesignation(fileUrl, overwriteEmployee, { resolve, reject }) {
      try {  
        const payload = {
          creator: this.$AuthUser.id,
          fileUrl,
        };
        const response = await this.$_importJobClassification(payload);
        resolve(response);
      } catch (error) {
        reject(error);
      }
    },
    async downloadJobClassificationTemplate(){
      try {
        const link = document.createElement('a');
        link.href = JOB_CLASSIFICATION_TEMPLATE_PATH;
        link.download = `Job Family Template`;
        // Appended the link to the body (necessary for Firefox)
        document.body.appendChild(link);
        link.click();

        // Removed the link after download
        document.body.removeChild(link);
      } catch (error) {
          return this.$toasted.error(`Error downloading file`,{duration: 6000});
      }
    },
    handlePage(pageNumber) {
      this.numberOfPage = pageNumber;
      this.queryUpdate();
    },
    handleItemsPerPage(itemNo) {
      this.itemsPerPage = itemNo;
      this.queryUpdate();
    },
    async openDrawer() {
      try {
        await this.$handlePrivilege("jobFamilies", "addNew");
        this.selectedJobFamily = null;
        this.showFamilyDrawer = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    async handleImport() {
      try {
        await this.$handlePrivilege("jobFamilies", "importJobFamily");
        this.isOpenImport = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    closeDrawer() {
      this.showFamilyDrawer = false;
      this.selectedJobFamily = null;
    },
    async handleEditJobFamily(jobFamily) {
      try {
        await this.$handlePrivilege("jobFamilies", "editJobFamily");
        this.selectedJobFamily = { ...jobFamily };
        this.showFamilyDrawer = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    async handleSaveFamily(payload) {
      if (!payload || !payload.jobFamilyName) {
        return;
      }
      if (payload.isEdit && payload.id) {
        await this.updateJobFamily(payload);
        return;
      }

      await this.createJobFamily(payload);
    },
    async updateJobFamily(payload) {
      try {
        await this.$_editJobClassification(payload.id, {
          name: payload.jobFamilyName,
          description: payload.description || "",
          type: "jobFamily",
        });
        this.$toasted.success("Job family updated successfully", {
          duration: 5000,
        });
        this.queryUpdate();
        this.closeDrawer();
      } catch (error) {
        this.$toasted.error("Unable to update job family", {
          duration: 5000,
        });
      }
    },
    async createJobFamily(payload) {
      try {
        await this.$_createJobClassification({
          name: payload.jobFamilyName,
          description: payload.description || "",
          type: "jobFamily",
        });
        this.$toasted.success("Job family created successfully", {
          duration: 5000,
        });
        this.queryUpdate();
        this.closeDrawer();
      } catch (error) {
        this.$toasted.error("Unable to create job family", {
          duration: 5000,
        });
      }
    },
    async openDeleteJobFamilyDialog(row) {
      if (!row || !row.id) {
        return;
      }
      try {
        await this.$handlePrivilege("jobFamilies", "deleteJobFamily");
        this.pendingDeleteJobFamilyId = row.id;
        this.pendingDeleteJobFamilyName = row.jobFamily || "";
        this.dialogDeleteJobFamily = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    closeDeleteJobFamilyDialog() {
      this.dialogDeleteJobFamily = false;
      this.pendingDeleteJobFamilyId = null;
      this.pendingDeleteJobFamilyName = "";
    },
    async confirmDeleteJobFamily() {
      if (!this.pendingDeleteJobFamilyId || this.deletingJobFamily) {
        return;
      }
      this.deletingJobFamily = true;
      try {
        await this.$_deleteJobClassification(this.pendingDeleteJobFamilyId);
        this.$toasted.success("Job family deleted successfully", {
          duration: 5000,
        });
        this.closeDeleteJobFamilyDialog();
        this.queryUpdate();
      } catch (error) {
        this.$toasted.error("Unable to delete job family", {
          duration: 5000,
        });
      } finally {
        this.deletingJobFamily = false;
      }
    },
    handleSearch(searchWord) {
      this.loadingData = true;
      if (searchWord) {
        this.searchWord = searchWord;
        this.queryUpdate();
      } else {
        this.searchWord = null;
        this.queryUpdate();
      }
    },
    handleSort(sortType) {
      this.loadingData = true;
      const changeSortType = sortType.split(":");
      let result;
      if (changeSortType[0] === "createdAt") {
        result = sortType;
      } else {
        result = `name:${changeSortType[1]}`;
      }
      this.sortType = result;
      this.queryUpdate();
    },
    handleCalendar() {},
    handleReload() {
      this.getJobClassifications("");
    },
  },
  async mounted() {
    try {
      await this.$handlePrivilege("jobFamilies", "viewRegister");
      this.showError = false;
      this.getJobClassifications();
    } catch (error) {
      this.showError = true;
      this.loadingData = false;
    }
  },
};
</script>

<style scoped>
.divider-line {
  border-left: 1px solid #878e99;
  height: 1.5rem;
}

.family-footer {
  border-top: 1px solid #0000001a;
}

.more:hover {
  background: #eeeeee;
}
</style>